import { Controller, UseFormReturn } from "react-hook-form";
import { TShiftSchema } from "../../Models";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dispatch, SetStateAction, useState } from "react";
import WeekdayButtons from "../../Components/WeekdayButtons";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { FormDatePicker } from "@/components/shared/form/DatePicker";
import { Separator } from "@/components/ui/separator";
import { TenderDurationFragment } from "@/graphql/__generated__/graphql";
import { FormattedNumberInputField } from "@/components/shared/form";
import { cn } from "@/lib/utils";

interface ShiftFormInputsProps {
  form: UseFormReturn<TShiftSchema>;
  tenderDuration: TenderDurationFragment;
  setUseOptionalDates?: Dispatch<SetStateAction<boolean>>;
}

function ShiftFormInputs({ form, tenderDuration, setUseOptionalDates }: ShiftFormInputsProps) {
  const { t } = useTranslation(["modals", "common", "translation"]);

  const {
    control,
    register,
    getValues,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = form;

  const [optionalDates, setOptionalDates] = useState(!!(getValues("startDate") || getValues("endDate")));

  // Watch holiday selection to disable holiday pay
  const watchHoliday = watch("holiday");
  const watchHolidayPay = watch("applyHolidayPay");


  const handleOptionalDatesChange = (checked: boolean) => {
    setOptionalDates(checked);
    if (!checked) {
      setValue("startDate", undefined);
      setValue("endDate", undefined);
      setUseOptionalDates?.(false);
    } else {
      setUseOptionalDates?.(true);
    }
  };

  return (
    <>
      <div className="flex-wrap gap-4 flex justify-between">
        <div className="form-container">
          <Label htmlFor="startTime" className={cn(errors.startTime && "text-red-500")}>
            {t("shift.inputs.starttime.label")}
          </Label>
          <Input {...register("startTime")} type="time" />
          {errors.startTime && <p className="input-error-message">{errors.startTime.message as string}</p>}
        </div>
        <div className="form-container">
          <Label htmlFor="endTime" className={cn(errors.endTime && "text-red-500")}>
            {t("shift.inputs.endtime.label")}
          </Label>
          <Input {...register("endTime")} type="time" />
          {errors.endTime && <p className="input-error-message">{errors.endTime.message as string}</p>}
        </div>
        <div className="form-container">
          <Label htmlFor="employeeQuantity" className={cn(errors.employeeQuantity && "text-red-500")}>
            {t("shift.inputs.employees.label")}
          </Label>
          <FormattedNumberInputField
            name="employeeQuantity"
            control={control}
            isInteger
            style="decimal"
            placeholder={t("shift.inputs.employees.placeholder")}
          />
          {errors.employeeQuantity && (
            <p className="input-error-message">{errors.employeeQuantity.message as string}</p>
          )}
        </div>

        <Controller
          name="applyHolidayPay"
          control={control}
          render={({ field }) => (
            <div className="form-container md:mt-6">
              <div className="flex items-center space-x-2 group relative">
                <Checkbox
                  id="applyHolidayPay"
                  checked={field.value || false}
                  onCheckedChange={field.onChange}
                  disabled={watchHoliday}
                />
                <Label
                  htmlFor="applyHolidayPay"
                  className={cn(errors.applyHolidayPay && "text-red-500")}
                >
                  {t("shift.inputs.applyholidaypay.label")}
                </Label>
                {watchHoliday && (
                  <p className="bg-white p-2 rounded-md border-[1px] border-slate-200 absolute bottom-4
                                  group-hover:opacity-100 opacity-0 text-xs text-red-500 z-10 -left-2 shadow-md"
                  >
                    {t("translation:shifts.holidaypay")}
                  </p>
                )}
              </div>
              {errors.applyHolidayPay && (
                <p className="input-error-message">{errors.applyHolidayPay.message as string}</p>
              )}
            </div>
          )}
        />
      </div>

      {/* Days Buttons */}
      <WeekdayButtons
        watch={watch}
        getValues={getValues}
        setValue={setValue}
        holidayPayEnabled={watchHolidayPay}
      />

      {errors.monday && <p className="input-error-message">{errors.monday.message as string}</p>}

      {/* Optional Start and End Date */}
      <div>
        <p className="text-sm font-semibold text-slate-400">{t("common:optional")}</p>
        <Separator className="mt-2 mb-2" />

        <div className="flex items-center gap-2 mb-2">
          <Checkbox
            id="optionalDates"
            checked={optionalDates}
            onCheckedChange={handleOptionalDatesChange}
          />
          <Label htmlFor="optionalDates" className="text-xs">{t("shift.labels.optionaldates")}</Label>
        </div>

        {optionalDates && (
          <div>
            <p className="text-xs text-gray-400">
              {t("shift.tooltips.optionaldatesfirst")}
            </p>
            <p className="text-xs text-gray-400 mb-2">
              {t("shift.tooltips.optionaldatessecond")}
            </p>
            <div className="flex-wrap gap-4 flex justify-between">
              {/* Start Date */}
              <div className="form-container">
                <Label htmlFor="startDate" className={cn(errors.startDate && "text-red-500")}>
                  {t("shift.inputs.startdate.label")}
                </Label>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <FormDatePicker
                      field={field}
                      allowOlderDates={true}
                      initialDate={new Date(tenderDuration.contract.startDate)}
                      minDate={new Date(tenderDuration.contract.startDate)}
                      maxDate={new Date(tenderDuration.contract.endDate)}
                      errorDisplay="form"
                      onValidationError={(message) => {
                        if (message) {
                          setError("startDate", { type: "manual", message });
                        } else {
                          clearErrors("startDate");
                        }
                      }}
                    />
                  )}
                />
                {errors.startDate && <p className="input-error-message">{errors.startDate.message as string}</p>}
              </div>

              {/* End Date */}
              <div className="form-container">
                <Label htmlFor="endDate" className={cn(errors.endDate && "text-red-500")}>
                  {t("shift.inputs.enddate.label")}
                </Label>
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <FormDatePicker
                      field={field}
                      allowOlderDates={true}
                      initialDate={new Date(tenderDuration.contract.endDate)}
                      minDate={new Date(tenderDuration.contract.startDate)}
                      maxDate={new Date(tenderDuration.contract.endDate)}
                      errorDisplay="form"
                      onValidationError={(message) => {
                        if (message) {
                          setError("endDate", { type: "manual", message });
                        } else {
                          clearErrors("endDate");
                        }
                      }}
                    />
                  )}
                />
                {errors.endDate && <p className="input-error-message">{errors.endDate.message as string}</p>}
              </div>
            </div>
            <Separator className="mt-4" />
          </div>
        )}
        <div className="mt-2">
          <p className="text-xs text-gray-400">
            {t("shift.tooltips.breaktimefirst")}
          </p>
          <p className="text-xs text-gray-400 mb-2">
            {t("shift.tooltips.breaktimesecond")}
          </p>
          <div className="flex-wrap gap-4 flex justify-between">
            {/* Pause Start Time */}
            <div className="form-container">
              <Label htmlFor="pauseStartTime" className={cn(errors.pauseStartTime && "text-red-500")}>
                {t("shift.inputs.pausestarttime.label")}
              </Label>
              <Input {...register("pauseStartTime")} type="time" />
              {errors.pauseStartTime && <p className="input-error-message">{errors.pauseStartTime.message as string}</p>}
            </div>

            {/* Pause End Time */}
            <div className="form-container">
              <Label htmlFor="pauseEndTime" className={cn(errors.pauseEndTime && "text-red-500")}>
                {t("shift.inputs.pauseendtime.label")}
              </Label>
              <Input {...register("pauseEndTime")} type="time" />
              {errors.pauseEndTime && <p className="input-error-message">{errors.pauseEndTime.message as string}</p>}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ShiftFormInputs;
